/**
 * Pull objectives into a year (HR UAT list #27): from the person's own objectives of the year
 * before, or from HR's Objective Library. Nothing rolls over by itself; the Team Member or
 * their People Leader ticks the ones to bring in, and each becomes a new objective for the
 * year, pending approval, with the KPIs ticked for it (the server copies them:
 * POST /objectives/copy).
 */
import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Building2, CheckCircle2, ChevronUp, History, Loader2, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { performanceService } from '@/services/api/performanceService'
import ObjectiveKpiPicker from './ObjectiveKpiPicker'
import { describeSelection, kpiChoice, type KpiSelection, type PickableObjective } from './kpiSelection'

export type CopySource = 'prior_year' | 'library'

interface CopyObjectivesPanelProps {
  source: CopySource
  employeeId: string
  memberName?: string
  /** The year the objectives are for. */
  year: number
  onBack: () => void
  /** After objectives were added. */
  onAdded: () => Promise<void> | void
}

const kpaLabel = (kpa: any): string | null => kpa?.display_name || kpa?.name || null

const pickableKpis = (kpis: any[] | undefined) =>
  (kpis ?? [])
    // A retired KPI isn't copied (the library lists it, flagged)
    .filter(k => k.is_active !== false && k.id !== undefined && k.id !== null)
    .map(k => ({ ...k, id: String(k.id) }))

export default function CopyObjectivesPanel({ source, employeeId, memberName, year, onBack, onAdded }: CopyObjectivesPanelProps) {
  const [offered, setOffered] = useState<PickableObjective[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [selection, setSelection] = useState<KpiSelection>({})
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)

  const fromLastYear = source === 'prior_year'

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setOffered(null)
      setFailed(false)
      setSelection({})
      try {
        const rows: PickableObjective[] = fromLastYear
          ? (await performanceService.getPriorYearObjectives(employeeId, year)).map(o => ({
              id: String(o.id),
              title: o.title,
              description: o.description,
              kpaName: kpaLabel(o.kpa),
              kpis: pickableKpis(o.kpis),
              alreadyAdded: o.already_added,
            }))
          : (await performanceService.getLibraryObjectives({ library_only: true, expand: 'kpa', limit: 500 })).map((o: any) => ({
              id: String(o.id),
              title: o.title,
              description: o.description ?? null,
              kpaName: kpaLabel(o.kpa),
              kpis: pickableKpis(o.kpis),
              alreadyAdded: false,
            }))
        if (!cancelled) setOffered(rows)
      } catch (error) {
        console.error(`Couldn't load objectives to pull in (${source}):`, error)
        if (!cancelled) setFailed(true)
      }
    }
    load()
    return () => { cancelled = true }
  }, [source, employeeId, year, fromLastYear])

  const shown = useMemo(() => {
    const query = search.trim().toLowerCase()
    return (offered ?? []).filter(o =>
      !query || o.title.toLowerCase().includes(query) || (o.description ?? '').toLowerCase().includes(query)
    )
  }, [offered, search])

  const chosenCount = Object.keys(selection).length

  const handleAdd = async () => {
    if (chosenCount === 0 || !offered) return
    setAdding(true)
    try {
      const kpiIds = kpiChoice(selection, offered)
      const result = await performanceService.copyObjectives({
        employee_id: employeeId,
        year,
        source,
        objective_ids: Object.keys(selection).map(Number),
        ...(kpiIds ? { kpi_ids: kpiIds } : {}),
      })
      const added = result.created.length
      const already = result.already_added.length
      toast.success(
        `Added ${added} objective${added === 1 ? '' : 's'} to ${year}, waiting for approval` +
        (already ? `. ${already} ${already === 1 ? 'was' : 'were'} already there.` : '.'),
        { duration: 5000 }
      )
      await onAdded()
    } catch (error) {
      // The API client's error carries only the HTTP status, so say what happened instead
      console.error('Copying objectives failed:', error)
      toast.error("The objectives couldn't be added. Nothing was changed. Please try again.")
    } finally {
      setAdding(false)
    }
  }

  const Icon = fromLastYear ? History : Building2
  const title = fromLastYear ? `${year - 1} objectives` : 'Objective Library'
  const who = memberName ? ` for ${memberName}` : ''
  const description = fromLastYear
    ? `Choose objectives from ${year - 1} to carry into ${year}${who}, and the KPIs each brings with its target. Each comes in waiting for approval; ${year - 1}'s stay as they are.`
    : `Choose objectives from HR's Objective Library to add to ${year}${who}, and the KPIs each brings. Each comes in waiting for approval.`
  const empty = fromLastYear
    ? `No ${year - 1} objectives to carry forward. Only objectives that were agreed in ${year - 1} can be brought in.`
    : 'The Objective Library is empty. HR adds objectives to it.'

  return (
    <div className="space-y-4">
      <Button variant="outline" onClick={onBack} className="mb-4">
        <ChevronUp className="h-4 w-4 mr-2 rotate-180" />
        Back
      </Button>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!fromLastYear && (offered?.length ?? 0) > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search the library..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search the library"
              />
            </div>
          )}

          {failed ? (
            <p className="text-sm text-red-700 py-6 text-center">
              The objectives couldn't be loaded. Go back and try again.
            </p>
          ) : offered === null ? (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading...
            </div>
          ) : offered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Icon className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{empty}</p>
            </div>
          ) : (
            <>
              <ObjectiveKpiPicker
                objectives={shown}
                selection={selection}
                onChange={setSelection}
                disabled={adding}
                alreadyAddedLabel={`Already in ${year}`}
                idPrefix={`copy-${source}`}
                ariaLabel={title}
              />
              {shown.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">No objectives match your search.</p>
              )}
            </>
          )}

          {(offered?.length ?? 0) > 0 && (
            <div className="flex gap-2 pt-4 border-t">
              <Button variant="outline" onClick={onBack} className="flex-1" disabled={adding}>
                Cancel
              </Button>
              <Button onClick={handleAdd} className="flex-1" disabled={chosenCount === 0 || adding}>
                {adding ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                {chosenCount === 0
                  ? `Choose objectives to add to ${year}`
                  : `Add ${describeSelection(selection)} to ${year}`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
